import { Stack, Box, Collapse } from '@mui/material';
import { Typography, colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import FilesList from './FilesList';
import FilesUploadButton from './FilesUploadButton';
import { AgentFile, AgentFileInvalid } from '../aiAgentsConsts';
import { useState } from 'react';
import { sortByDate } from '../../../utils/sortByDate';
import { SortOrder } from '../../common/types';
import { Close, PlusFile, Info } from '@sweep-io/sweep-design/dist/icons';
import { usePermissions } from '../../../permissions/usePermissions';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../../reducers/userInfoReducer';
import { AgentFileStatus } from '@server/ai';

const AgentFiles = ({
  files,
  addFiles,
  removeFile,
  agentName,
  closeDialog,
}: {
  files: AgentFile[];
  addFiles: (files: File[]) => Promise<void>;
  removeFile: (fileIdToRemove: string) => void;
  agentName: string;
  closeDialog: () => void;
}) => {
  const [invalidFiles, setInvalidFiles] = useState<AgentFileInvalid[]>([]);
  const { evaluatePermissions } = usePermissions();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const [isAllowedToEditAgent] = evaluatePermissions([
    { permission: 'create:ai-agent:file', crmOrgId: crmOrgId ?? '' },
  ]);
  const [isAllowedToUploadFiles] = evaluatePermissions([
    { permission: 'create:ai-agent:file', crmOrgId: crmOrgId ?? '' },
  ]);
  const [isAllowedToDeleteFiles] = evaluatePermissions([
    { permission: 'delete:ai-agent:file', crmOrgId: crmOrgId ?? '' },
  ]);

  const onFilesAdd = async (addedFiles: File[]) => {
    if (!isAllowedToUploadFiles) return;
    await addFiles(addedFiles);
  };

  const onRemove = (fileIdToRemove: string) => {
    if (!isAllowedToDeleteFiles) return;
    removeFile(fileIdToRemove);
  };

  const onRemoveInvalidFile = (fileIdToRemove: string) => {
    setInvalidFiles(invalidFiles.filter((file) => file.id !== fileIdToRemove));
  };

  const onFilesAddInvalid = (files: AgentFileInvalid[]) => {
    setInvalidFiles(files);
  };

  const renderEmptyState = files.length === 0;
  const sortedFiles = files.toSorted((a, b) =>
    sortByDate(a.createdAt, b.createdAt, SortOrder.DESC),
  );
  
  const completedFilesCount = files.filter(
    (file) => !file.status || file.status === AgentFileStatus.COMPLETED,
  ).length;

  const processingFilesCount = files.filter(
    (file) => file.status === AgentFileStatus.PROCESSING,
  ).length;

  return (
    <Stack flex={1} p={2} gap={2}>
      <Stack
        direction="row"
        sx={{
          height: '32px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h3">Files</Typography>
        <Stack direction="row" gap={1} alignItems="center">
          {!renderEmptyState && (
            <Tooltip
              title={
                !isAllowedToUploadFiles
                  ? 'To manage agent files, please contact your admin'
                  : undefined
              }
            >
              <span>
                <FilesUploadButton
                  startIconName="Files"
                  onAddValidFiles={onFilesAdd}
                  onAddInvalidFiles={onFilesAddInvalid}
                  size="small"
                  disabled={!isAllowedToUploadFiles}
                />
              </span>
            </Tooltip>
          )}
          <IconButton onClick={closeDialog} variant="flat" size="small">
            <Close />
          </IconButton>
        </Stack>
      </Stack>
      {renderEmptyState && (
        <EmptyState
          onAddValidFiles={onFilesAdd}
          onAddInvalidFiles={onFilesAddInvalid}
          agentName={agentName}
          isAllowedToEditAgent={isAllowedToEditAgent}
        />
      )}
      {!renderEmptyState && (
        <>
          {completedFilesCount > 0 && (
            <Typography variant="body" color={colors.grey[700]}>
              {completedFilesCount} Uploaded files
            </Typography>
          )}
          <Collapse in={processingFilesCount > 0}>
            <Box
              sx={{
                backgroundColor: colors.blue[100],
                borderRadius: '8px',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <Stack direction="row" gap={1} alignItems="center">
                <Info size="small" />
                <Typography variant="caption-bold">
                  Uploading your {processingFilesCount === 1 ? 'file' : 'files'}
                </Typography>
              </Stack>
              <Typography variant="caption" color={colors.grey[700]}>
                This may take a few minutes. You can close this window, we'll keep processing in
                the background and notify you when it's done.
              </Typography>
            </Box>
          </Collapse>
          <FilesList
            files={sortedFiles}
            invalidFiles={invalidFiles}
            onRemove={onRemove}
            onRemoveInvalidFile={onRemoveInvalidFile}
          />
        </>
      )}
    </Stack>
  );
};

const EmptyState = ({
  onAddValidFiles,
  onAddInvalidFiles,
  agentName,
  isAllowedToEditAgent,
}: {
  onAddValidFiles: (files: File[]) => void;
  onAddInvalidFiles: (agentFiles: AgentFileInvalid[]) => void;
  agentName: string;
  isAllowedToEditAgent: boolean;
}) => {
  return (
    <Stack height="100%" p={2} gap={2} alignItems="center" justifyContent="center">
      <Stack alignItems="center" gap={1}>
        <PlusFile size="medium" />
        <Typography variant="h5" color={colors.grey[800]}>
          Add data, reports, business documents, and more
        </Typography>
        <Box maxWidth="350px" textAlign="center">
          <Typography variant="body" color={colors.grey[700]}>
            {agentName} can access their content when you chat with the agent
          </Typography>
        </Box>
      </Stack>
      <Box>
        <Tooltip
          title={
            !isAllowedToEditAgent ? 'To manage agent files, please contact your admin' : undefined
          }
        >
          <span>
            <FilesUploadButton
              startIconName="Plus"
              onAddValidFiles={onAddValidFiles}
              onAddInvalidFiles={onAddInvalidFiles}
              size="small"
              disabled={!isAllowedToEditAgent}
            />
          </span>
        </Tooltip>
      </Box>
    </Stack>
  );
};

export default AgentFiles;
